"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useIntercomStore } from "@/stores/use-intercom-store"
import * as Sentry from "@sentry/nextjs"
import { Button } from "@ui/shad"
import { HelpCircle, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { DOCS_URL } from "@/lib/docs"
import { useFetchCurrentUser } from "@/hooks/api/users/queries"

import { isWaiting, reduce, SHOW_TIMEOUT_MS, type LauncherEvent, type LauncherPhase } from "./intercom-launcher-state"

const FRONTEND_RELEASE =
  process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || process.env.NEXT_PUBLIC_SENTRY_RELEASE || "unknown"

type OpenAttempt = {
  id: string
  clickedAt: string
  clickedPhase: LauncherPhase
}

function openHelpCenter() {
  window.open(DOCS_URL, "_blank", "noopener,noreferrer")
}

export function IntercomLauncher() {
  const status = useIntercomStore((state) => state.status)
  const { data: user } = useFetchCurrentUser()
  const [unreadCount, setUnreadCount] = useState(0)
  const [phase, setPhase] = useState<LauncherPhase>("idle")
  const launcherRef = useRef<HTMLDivElement>(null)
  const phaseRef = useRef<LauncherPhase>("idle")
  const showTimerRef = useRef<number | null>(null)
  const attemptRef = useRef<OpenAttempt | null>(null)
  const runRef = useRef<(event: LauncherEvent) => void>(() => undefined)

  const isReady = status === "ready"
  const isLoading = status === "idle" || status === "loading"
  const isError = status === "error"

  const telemetry = useCallback(
    (attempt: OpenAttempt | null, launcherPhase: LauncherPhase) => ({
      attemptId: attempt?.id,
      clickedAt: attempt?.clickedAt,
      clickedPhase: attempt?.clickedPhase,
      launcherPhase,
      providerStatus: status,
      frontendRelease: FRONTEND_RELEASE,
      userId: user?.id,
      intercomUserId: user?.intercom_user_id,
      intercomCompanyId: user?.intercom_company_id,
    }),
    [status, user]
  )

  const clearShowTimer = () => {
    if (showTimerRef.current === null) return
    window.clearTimeout(showTimerRef.current)
    showTimerRef.current = null
  }

  const showFallback = () => {
    toast.error("Support chat couldn’t open", {
      description: "Refresh the page to try again, or use the help center.",
      action: { label: "Open help center", onClick: openHelpCenter },
    })
  }

  const run = (event: LauncherEvent) => {
    const previousPhase = phaseRef.current
    const { phase: nextPhase, effect } = reduce(previousPhase, event)
    phaseRef.current = nextPhase
    setPhase(nextPhase)

    if (event.type === "onShow") {
      clearShowTimer()
      if (effect === "showConfirmed") {
        Sentry.addBreadcrumb({
          category: "intercom-launcher",
          message: "Intercom messenger confirmed visible",
          level: "info",
          data: {
            ...telemetry(attemptRef.current, nextPhase),
            outcome: "shown",
            confirmedAt: new Date().toISOString(),
          },
        })
      }
      attemptRef.current = null
      return
    }

    if (effect === "show") {
      clearShowTimer()
      showTimerRef.current = window.setTimeout(() => runRef.current({ type: "showTimeout" }), SHOW_TIMEOUT_MS)
      window.Intercom?.("show")
      return
    }

    if (nextPhase !== "pending") clearShowTimer()

    switch (effect) {
      case "hide":
        window.Intercom?.("hide")
        break
      case "openDocs":
        Sentry.captureMessage("Intercom launcher clicked while unavailable — using fallback", {
          level: "warning",
          tags: { feature: "intercom-launcher", outcome: "unavailable" },
          extra: telemetry(attemptRef.current, previousPhase),
        })
        attemptRef.current = null
        openHelpCenter()
        break
      case "queuedFailed":
      case "showTimedOut":
        Sentry.captureMessage(
          effect === "showTimedOut"
            ? "Intercom messenger did not confirm show before timing out"
            : "Intercom launcher open could not complete — boot failed",
          {
            level: effect === "showTimedOut" ? "error" : "warning",
            tags: { feature: "intercom-launcher", outcome: effect === "showTimedOut" ? "timeout" : "boot-failed" },
            extra: {
              ...telemetry(attemptRef.current, previousPhase),
              failedAt: new Date().toISOString(),
              timeoutMs: effect === "showTimedOut" ? SHOW_TIMEOUT_MS : undefined,
            },
          }
        )
        attemptRef.current = null
        showFallback()
        break
    }
  }
  runRef.current = run

  const dispatch = useCallback((event: LauncherEvent) => runRef.current(event), [])

  const handleClick = useCallback(() => {
    const clickedPhase = phaseRef.current

    if (clickedPhase === "idle") {
      attemptRef.current = {
        id: crypto.randomUUID(),
        clickedAt: new Date().toISOString(),
        clickedPhase,
      }
    }

    Sentry.addBreadcrumb({
      category: "intercom-launcher",
      message: "Intercom launcher clicked",
      level: "info",
      data: {
        ...telemetry(attemptRef.current, clickedPhase),
        action: clickedPhase === "visible" ? "hide" : "show",
      },
    })
    dispatch({ type: "click", status })
  }, [dispatch, status, telemetry])

  // Register callbacks before the status effect can fulfill a queued open request.
  useEffect(() => {
    if (!isReady || !window.Intercom) return
    window.Intercom("onUnreadCountChange", (count) => setUnreadCount(count))
    window.Intercom("onHide", () => dispatch({ type: "onHide" }))
    window.Intercom("onShow", () => dispatch({ type: "onShow" }))
  }, [isReady, dispatch])

  useEffect(() => {
    dispatch({ type: "statusChange", status })
  }, [status, dispatch])

  useEffect(() => clearShowTimer, [])

  const isVisible = phase === "visible"

  useEffect(() => {
    document.documentElement.dataset.intercomOpen = isVisible ? "true" : "false"
    return () => {
      delete document.documentElement.dataset.intercomOpen
    }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible) return
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (!target) return
      if (launcherRef.current?.contains(target)) return
      if (target.closest(".intercom-messenger-frame")) return
      dispatch({ type: "dismiss" })
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [isVisible, dispatch])

  useEffect(() => {
    const handleToggle = () => handleClick()
    window.addEventListener("toggle-intercom-chat", handleToggle)
    return () => window.removeEventListener("toggle-intercom-chat", handleToggle)
  }, [handleClick])

  const tooltip = isError ? "Support chat is unavailable — open help docs" : isLoading ? "Loading support…" : undefined

  return (
    <div ref={launcherRef} className="relative">
      <Button variant="ghost" size="iconSm" onClick={handleClick} tooltip={tooltip} aria-label="Support">
        {isWaiting(phase) ? <Loader2 className="animate-spin" /> : <HelpCircle />}
      </Button>
      {unreadCount > 0 && (
        <div className="bg-destructive text-background absolute top-0 right-0 flex size-3.5 items-center justify-center rounded-full text-xs font-medium">
          {unreadCount > 9 ? "9+" : unreadCount}
        </div>
      )}
    </div>
  )
}
